import { readonly, ref, type Ref } from 'vue';

/**
 * AB#22137 — phone layout. One breakpoint for the whole mobile work: below
 * 768px wide. It is the same line App.vue draws for the bottom bar
 * (`innerWidth <= 767`) and the same as Tailwind's `md`, so a component that
 * switches in script (here) and one that switches in CSS
 * (`@media (max-width: 767.98px)`) always agree.
 *
 * Everything behind this flag is phone-only: on a desktop it is false and the
 * markup is exactly what it was before.
 *
 * A module-level singleton: one media-query listener for the app, however many
 * components ask.
 */
export const PHONE_QUERY = '(max-width: 767.98px)';

const phone = ref(false);
let started = false;

const start = (): void => {
  if (started) return;
  started = true;
  try {
    const mq = window.matchMedia(PHONE_QUERY);
    phone.value = mq.matches;
    const onChange = (e: MediaQueryListEvent): void => {
      phone.value = e.matches;
    };
    if (typeof mq.addEventListener === 'function') mq.addEventListener('change', onChange);
    else mq.addListener?.(onChange);
  } catch {
    // No window or no matchMedia (tests): not a phone.
    phone.value = false;
  }
};

export const usePhone = (): Readonly<Ref<boolean>> => {
  start();
  return readonly(phone);
};

/** For tests: set the flag directly. */
export const __setPhoneForTests = (value: boolean): void => {
  started = true;
  phone.value = value;
};
